/* Editar un artículo de Stock desde el hub: la regla, sin React ni Supabase.
 *
 * Igna, 23/10: «Necesitamos que los productos que se cargan tanto a mano como
 * con código de barras se puedan editar desde el hub». Los que da de alta el
 * escaneo (FlotaBot, `/escaneo`) nacen con lo mínimo —tipo INSUMO, la unidad,
 * y de nombre lo que se leyó, a veces «6935482103519     HPCF283A»— y sin
 * marca. Corregirlos es el trabajo de todos los días y el formulario no
 * dejaba: pedía la marca para guardar cualquier otra cosa.
 *
 * Acá vive lo que decide qué se pide, cómo se lee un código de barras escrito
 * a mano y qué hay que escribir en `stock_codigos`; el formulario
 * (`FormStock`) y el servicio (`services/stockCodigos`) sólo lo usan.
 */
import { unidadesOptions } from "../components/formularios/data/OptionsContent";

const vacio = (v) =>
  v === null || v === undefined || String(v).trim() === "";

/* ── AL EDITAR, LO QUE YA ESTABA VACÍO NO TRABA ──────────────────────────────
 *
 * Al dar de alta los obligatorios son obligatorios. Al editar, un campo
 * obligatorio que la fila YA tenía vacío no frena el guardado: el artículo
 * existe así desde hace rato (los del escaneo, los que vinieron de Firestore),
 * y exigirlo convierte «corregir la descripción» en «inventar un dato para
 * poder corregir la descripción». Lo que sí frena es BORRAR un obligatorio
 * que tenía valor: eso no es completar, es romper la ficha.
 */
export function obligatoriosAlEditar(campos = [], original = null) {
  if (!original) return campos;
  return campos.map((cp) =>
    cp.important && vacio(original[cp.key]) ? { ...cp, important: false } : cp,
  );
}

/* «UN» → «UNIDADES». La unidad se guarda escrita entera (así están 1.826 de
 * los artículos y así la ofrece el desplegable), pero los dos que dio de alta
 * FlotaBot antes del 20/10 quedaron con la sigla. Con la sigla el desplegable
 * se dibuja vacío —«Seleccionar…»— aunque la fila tenga unidad, y parece que
 * falta un dato que está. Lo que no es una sigla conocida vuelve tal cual. */
export function unidadParaElFormulario(unidad) {
  const u = String(unidad ?? "").trim().toUpperCase();
  if (!u) return "";
  const porSigla = unidadesOptions()[u];
  return porSigla ? porSigla.descripcion : unidad;
}

/* ── LOS CÓDIGOS DE BARRAS, LEÍDOS COMO LOS LEE FLOTABOT ─────────────────────
 *
 * Es la misma normalización que `panol_codigos.leer_codigo` (FlotaBot) y la
 * que fija el `check` de `stock_codigos`. Tiene que ser la MISMA: si acá
 * «7799115000779» se guardara así y el escaneo lo busca como
 * «07799115000779», el código cargado desde el hub no lo encontraría nadie.
 *
 *   · 12, 13 o 14 cifras con el verificador bien → GTIN de 14 con ceros
 *     adelante. Con el verificador mal → «mal leído»: no se guarda.
 *   · 8 cifras con el verificador bien → GTIN; mal → número de pieza.
 *   · Lo demás (Code 128, número de pieza) → sólo [0-9A-Z], 6 a 48.
 *   · El código del hub (IN010004) no se guarda: ya es el id del artículo.
 */
const PREFIJOS = ["AC", "CA", "CB", "EL", "FL", "FR", "HE", "HI", "IN", "MT",
  "RC", "RF", "RO", "SP", "SV", "TR"];
const INTERNO = new RegExp(`^(?:${PREFIJOS.join("|")})[0-9][0-9X][0-9]{4}$`);
const SOLO_DIGITOS = /^\d(?:[\d\s.-]*\d)?$/;
const PALABRA = /^[0-9A-Za-z](?:[0-9A-Za-z\-./]*[0-9A-Za-z])?$/;
const GS1 = /^\(01\)(\d{14})|^01(\d{14})./;
const LARGO_MIN = 6;
const LARGO_MAX = 48;

export function digitoOk(digitos) {
  const d = String(digitos ?? "");
  if (!/^\d{2,}$/.test(d)) return false;
  const cuerpo = d.slice(0, -1).split("").reverse();
  const suma = cuerpo.reduce((s, c, i) => s + Number(c) * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (suma % 10)) % 10 === Number(d.slice(-1));
}

const deDigitos = (d) => {
  const n = d.length;
  if (n >= 12 && n <= 14) {
    return digitoOk(d)
      ? { forma: "gtin", codigo: d.padStart(14, "0") }
      : { forma: "mal_leido", codigo: d };
  }
  if (n === 8) {
    return digitoOk(d)
      ? { forma: "gtin", codigo: d.padStart(14, "0") }
      : { forma: "otro", codigo: d };
  }
  if (n >= LARGO_MIN && n <= LARGO_MAX) return { forma: "otro", codigo: d };
  return null;
};

/* `{forma, codigo}` o `null`. `forma` ∈ gtin | otro | mal_leido | interno. */
export function leerCodigoDeBarras(texto) {
  /* Sin caracteres de control: un lector USB termina con Enter o con un GS
     invisible. Se filtran por código y no con una regex de controles, que el
     lint (`no-control-regex`) no deja escribir. */
  const sinControl = Array.from(String(texto ?? ""))
    .filter((ch) => {
      const n = ch.codePointAt(0);
      return n > 0x1f && n !== 0x7f && !(n >= 0x200b && n <= 0x200f) && n !== 0xfeff;
    })
    .join("");
  const crudo = sinControl
    .trim()
    .replace(/^["'«»“”‘’`]+|["'«»“”‘’`]+$/g, "")
    .trim();
  if (!crudo) return null;
  if (INTERNO.test(crudo.toUpperCase())) {
    return { forma: "interno", codigo: crudo.toUpperCase() };
  }
  const g = crudo.match(GS1);
  const gtin = g && (g[1] || g[2]);
  if (gtin && digitoOk(gtin)) return { forma: "gtin", codigo: gtin };
  if (SOLO_DIGITOS.test(crudo)) return deDigitos(crudo.replace(/\D/g, ""));
  if (PALABRA.test(crudo)) {
    const c = crudo.replace(/[^0-9A-Za-z]/g, "").toUpperCase();
    if (INTERNO.test(c)) return { forma: "interno", codigo: c };
    if (/^\d+$/.test(c)) return deDigitos(c);
    if (c.length >= LARGO_MIN && c.length <= LARGO_MAX && /\d/.test(c)) {
      return { forma: "otro", codigo: c };
    }
  }
  return null;
}

/* Lo que se le contesta a la persona cuando lo que escribió no se puede
 * guardar. `null` si se puede. */
export function problemaDelCodigo(lc) {
  if (!lc) return "Eso no parece un código de barras: tiene que tener números (6 a 48 letras y números).";
  if (lc.forma === "mal_leido") return "Los números no cierran (el último dígito no coincide): revisalo, puede faltar o sobrar uno.";
  if (lc.forma === "interno") return "Ése es un código del hub, no de barras: ya es el código de un artículo.";
  return null;
}

/* El código como está impreso: el GTIN de 14 que empieza con 0 es el EAN-13
 * de los 13 de atrás. Es lo que la persona tiene en la caja para comparar. */
export function mostrarCodigo(codigo) {
  const c = String(codigo ?? "");
  return /^0\d{13}$/.test(c) ? c.slice(1) : c;
}

/* Qué hay que escribir en `stock_codigos`: los que estaban y ya no
 * (`quitar`) y los nuevos (`agregar`). Comparando contra lo que se leyó al
 * abrir, guardar sin tocar los códigos no escribe nada. */
export function cambiosDeCodigos(originales = [], actuales = []) {
  const antes = new Set(originales.map((c) => c.codigo));
  const ahora = new Set(actuales.map((c) => c.codigo));
  return {
    quitar: originales.filter((c) => !ahora.has(c.codigo)).map((c) => c.codigo),
    agregar: actuales.filter((c) => !antes.has(c.codigo)),
  };
}
